import {
  OverflowTooltip,
  Table,
  useFetch,
  useNotificationContext,
  UsePrivileges,
} from "@4uhub/lib4uhub";
import ReplayIcon from "@mui/icons-material/Replay";
import { Box, CircularProgress, IconButton, Tooltip } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { useConfirm } from "material-ui-confirm";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import {
  formatExecutionDate,
  formatExecutionDateTime,
} from "../../../../../components/Dashboards/Executions/utils";
import {
  IDashboardGenerate,
  IDashboardGenerateExecution,
} from "../../../../../models/dashboard-generate-execution";
import {
  dashboardGenerateService,
  getDashboardGenerateExecutionById,
  reprocessDashboardGenerateExecution,
} from "../../../../../services/dashboardGenerateExecution.service";
import GenerateStatusChip from "../../../../../components/Dashboards/Executions/GenerateStatusChip";
import ExecutionDetailHeader from "./ExecutionDetailHeader";

const translationPath = "page.dashboardExecutions.detail.";

const STATUS_ERROR_CODE = "4";

const ExecutionDetailPage = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const { id } = useParams();

  const [execution, setExecution] =
    useState<IDashboardGenerateExecution | null>(null);

  const { sendRequest } = useFetch(getDashboardGenerateExecutionById);

  const fetchExecution = useCallback(async () => {
    if (!id) return;
    const { data, success } = await sendRequest(id);
    if (success && data) setExecution(data);
  }, [id, sendRequest]);

  useEffect(() => {
    fetchExecution();
  }, [fetchExecution]);

  const [refresh, setRefresh] = useState(0);

  const confirm = useConfirm();

  const { setMessage } = useNotificationContext();

  const { privileges } = UsePrivileges();
  const canReprocess = useMemo(
    () =>
      privileges.some(
        (p) =>
          p.domainName === "all" ||
          (p.domainName === "dashboardcomponent" &&
            (p.actions.includes("Update") || p.actions.includes("All"))),
      ),
    [privileges],
  );

  const { sendRequest: reprocessRequest } = useFetch(
    reprocessDashboardGenerateExecution,
  );

  const reprocessHandler = useCallback(
    async (dashboardGenerateId?: string) => {
      if (!id) return;

      try {
        await confirm({
          confirmationButtonProps: { variant: "contained" },
          title: t(translationPath + "reprocess_confirm.title"),
          description: t(
            translationPath +
              (dashboardGenerateId
                ? "reprocess_confirm.description_one"
                : "reprocess_confirm.description_all"),
          ),
          cancellationText: t(translationPath + "reprocess_confirm.cancel"),
          confirmationText: t(translationPath + "reprocess_confirm.confirm"),
        });
      } catch (error) {
        return;
      }

      const { data, success } = await reprocessRequest({
        id,
        dashboardGenerateId,
      });
      if (!success) return;

      const count = data?.reprocessedGenerates ?? 0;
      setMessage({
        message:
          count > 0
            ? t(translationPath + "reprocess_success", { count })
            : t(translationPath + "reprocess_nothing"),
        type: count > 0 ? "success" : "warning",
        notificationKey: new Date().getMilliseconds(),
      });

      fetchExecution();
      setRefresh((prev) => prev + 1);
    },
    [id, confirm, t, reprocessRequest, setMessage, fetchExecution],
  );

  const renderOverflowCell = useCallback((value?: string | null) => {
    const text = value || "-";

    return (
      <OverflowTooltip
        text={text}
        tooltip={text}
        textTransform="none"
        variant="body2"
      />
    );
  }, []);

  const columns: GridColDef<IDashboardGenerate>[] = useMemo(
    () => [
      {
        field: "dashboardEstablishmentComponent",
        headerName: t(translationPath + "component") || "component",
        flex: 2.2,
        minWidth: 280,
        sortable: false,
        renderCell: (params) => {
          const component = params.row.dashboardEstablishmentComponent;
          return renderOverflowCell(
            [component?.name].filter(Boolean).join(" - "),
          );
        },
      },
      {
        field: "dashboardGroupType",
        headerName: t(translationPath + "group_type") || "group_type",
        width: 110,
        sortable: false,
        renderCell: (params) => params.row.dashboardGroupType?.name ?? "-",
      },
      {
        field: "initialDate",
        headerName: t(translationPath + "period") || "period",
        minWidth: 190,
        sortable: false,
        renderCell: (params) =>
          `${formatExecutionDate(params.row.initialDate, language)} - ${formatExecutionDate(params.row.endDate, language)}`,
      },
      {
        field: "statusGenerateDashboard",
        headerName: t(translationPath + "status") || "status",
        minWidth: 140,
        sortable: false,
        renderCell: (params) => (
          <GenerateStatusChip status={params.row.statusGenerateDashboard} />
        ),
      },
      {
        field: "retries",
        headerName: t(translationPath + "retries") || "retries",
        maxWidth: 100,
        sortable: false,
      },
      {
        field: "endProcessDate",
        headerName:
          t(translationPath + "end_process_date") || "end_process_date",
        minWidth: 150,
        sortable: false,
        renderCell: (params) => formatExecutionDateTime(params.value, language),
      },
      {
        field: "error",
        headerName: t(translationPath + "error") || "error",
        flex: 1.6,
        minWidth: 260,
        sortable: false,
        renderCell: (params) => renderOverflowCell(params.value),
      },
      ...(canReprocess
        ? [
            {
              field: "reprocess",
              headerName: "",
              width: 60,
              sortable: false,
              align: "center" as const,
              renderCell: (params: { row: IDashboardGenerate }) =>
                params.row.statusGenerateDashboard?.code ===
                STATUS_ERROR_CODE ? (
                  <Tooltip title={t(translationPath + "reprocess_one")}>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        reprocessHandler(params.row.id);
                      }}
                    >
                      <ReplayIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                ) : null,
            },
          ]
        : []),
    ],
    [t, language, renderOverflowCell, canReprocess, reprocessHandler],
  );

  const serviceProps = useMemo(
    () => ({ DashboardGenerateExecutionId: id, refresh }),
    [id, refresh],
  );

  if (!execution) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{ display: "flex", flexDirection: "column", gap: 2, height: "100%" }}
    >
      <ExecutionDetailHeader
        execution={execution}
        onReprocess={
          canReprocess &&
          execution.failedGenerates > 0 &&
          !execution.generatesPurgedDate
            ? () => reprocessHandler()
            : undefined
        }
      />
      {}
      <Box
        sx={{
          flex: 1,
          minHeight: 400,
          "& .MuiDataGrid-columnHeaderCheckbox, & .MuiDataGrid-cellCheckbox": {
            display: "none !important",
          },
        }}
      >
        <Table<IDashboardGenerate>
          title={t(translationPath + "title")}
          columns={columns}
          service={dashboardGenerateService}
          serviceProps={serviceProps}
          searchInputPlaceHolder={t(translationPath + "search_placeholder")}
          showDefaultMacroFilters={false}
          showButton={false}
          showDeleteButton={false}
          defaultActions={false}
          hideLog
          onRowClick={() => {}}
          getRowId={(row) => row.id}
        />
      </Box>
    </Box>
  );
};

export default ExecutionDetailPage;
